import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { HelpIconComponent } from '../../core/labels/help-icon.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import {
  DIARY_JURIS_NOTIFY_KEY,
  DIARY_JURIS_NOTIFY_TYPE,
  DIARY_PREF_ALLOW_STATUS_UPDATE,
  DIARY_PREF_TYPE,
  normalizeDiaryYesNoPreference,
  readDiaryYesNoPreference,
} from './diary-storage.keys';

@Component({
  selector: 'app-shared-diary-pref-dialog',
  imports: [
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    DialogComponent,
    RadioButtonComponent,
    HelpIconComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-dialog #dialog label="Shared diaries preferences" width="40rem">
      <ng-template #content>
        <app-loading-panel
          [state]="loading ? 'Loading' : 'Loaded'"
          loadingStyle="Box"
          [loadingHeight]="220"
        >
          <ng-template #loaded>
            <div *ngTemplateOutlet="body"></div>
          </ng-template>
        </app-loading-panel>
      </ng-template>
    </app-dialog>
    <ng-template #body>
      <form [formGroup]="fg" class="tw-min-w-0">
        <div
          class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start tw-gap-3 sm:tw-gap-4 tw-pb-4 tw-min-w-0"
        >
          <div
            class="tw-flex tw-gap-2 tw-items-start tw-min-w-0 sm:tw-grow sm:tw-pt-2"
          >
            <span class="label tw-font-bold tw-break-words tw-min-w-0">
              Notify me of OmniInsights diaries created in JURIS
            </span>
            <app-help-icon
              class="tw-shrink-0"
              [helpText]="jurisNotifyHelpText"
              ariaLabel="Notify me of OmniInsights diaries created in JURIS information"
              size="20px"
            />
          </div>
          <div class="tw-shrink-0 sm:tw-pt-2">
            <app-radio-button
              [options]="yesNoOptions"
              optionValue="value"
              optionLabel="description"
              [control]="fg.controls.jurisNotify"
              screenReaderLabel="Notify me of OmniInsights diaries created in JURIS"
              [announceGroupLabelWithOptions]="true"
              [vertical]="false"
              [showHelpText]="false"
            />
          </div>
        </div>
        <div
          class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start tw-gap-3 sm:tw-gap-4 tw-pb-4 tw-min-w-0"
        >
          <div
            class="tw-flex tw-gap-2 tw-items-start tw-min-w-0 sm:tw-grow sm:tw-pt-2"
          >
            <span class="label tw-font-bold tw-break-words tw-min-w-0">
              Allow other users to update the diary status
            </span>
          </div>
          <div class="tw-shrink-0 sm:tw-pt-2">
            <app-radio-button
              [options]="yesNoOptions"
              optionValue="value"
              optionLabel="description"
              [control]="fg.controls.allowStatusUpdate"
              screenReaderLabel="Allow other users to update the diary status"
              [announceGroupLabelWithOptions]="true"
              [vertical]="false"
              [showHelpText]="false"
            />
          </div>
        </div>
      </form>
      <app-action-row useMobileLayout classes="tw-pt-4">
        <app-button
          left1
          label="Restore defaults"
          emphasis="Low"
          (click)="restoreDefaults()"
        />
        <app-button
          right1
          label="Save"
          [disabled]="loading || !hasChanges"
          (click)="savePref()"
        />
        <app-button right2 label="Cancel" emphasis="Low" (click)="close()" />
      </app-action-row>
    </ng-template>
  `,
  styles: ``,
})
export class SharedDiaryPrefDialogComponent
  extends BaseComponent
  implements OnInit
{
  private snackbar = inject(Snackbar);

  @ViewChild('dialog')
  dialog: DialogComponent;

  jurisNotifyHelpText =
    'When enabled, you will receive email notifications for OmniInsights diaries created in JURIS.';

  yesNoOptions = [
    { description: 'No', value: 'N' },
    { description: 'Yes', value: 'Y' },
  ];

  fg = new FormGroup({
    jurisNotify: new FormControl<string>('N'),
    allowStatusUpdate: new FormControl<string>('Y'),
  });

  hasChanges = false;
  loading = false;
  initialValues: { jurisNotify: string; allowStatusUpdate: string } | null =
    null;

  ngOnInit(): void {
    this.dispose.subs = this.fg.valueChanges.subscribe(() => {
      this.hasChanges = this.checkForChanges();
    });
  }

  checkForChanges(): boolean {
    if (!this.initialValues) {
      return false;
    }
    const current = this.fg.value;
    return (
      current.jurisNotify !== this.initialValues.jurisNotify ||
      current.allowStatusUpdate !== this.initialValues.allowStatusUpdate
    );
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async show(): Promise<void> {
    this.dialog.visible = true;
    await this.loadPreferences();
  }

  @HandleLoadingState()
  async loadPreferences(): Promise<void> {
    this.loading = true;
    try {
      const [jurisPrefs, allowPrefs] = await Promise.all([
        this.api.preferences.getPreferences({
          type: DIARY_JURIS_NOTIFY_TYPE,
          key: DIARY_JURIS_NOTIFY_KEY,
          value: '',
        }),
        this.api.preferences.getPreferences({
          type: DIARY_PREF_TYPE,
          key: DIARY_PREF_ALLOW_STATUS_UPDATE,
          value: '',
        }),
      ]);

      const jurisNotify = readDiaryYesNoPreference(
        jurisPrefs,
        DIARY_JURIS_NOTIFY_KEY,
        'N',
      );
      const allowStatusUpdate = readDiaryYesNoPreference(
        allowPrefs,
        DIARY_PREF_ALLOW_STATUS_UPDATE,
        'Y',
      );

      this.fg.controls.jurisNotify.setValue(jurisNotify, { emitEvent: false });
      this.fg.controls.allowStatusUpdate.setValue(allowStatusUpdate, {
        emitEvent: false,
      });
      this.initialValues = { jurisNotify, allowStatusUpdate };
      this.hasChanges = false;
    } finally {
      this.loading = false;
    }
  }

  close(): void {
    this.dialog.visible = false;
  }

  restoreDefaults(): void {
    this.fg.controls.jurisNotify.setValue('N');
    this.fg.controls.allowStatusUpdate.setValue('Y');
  }

  @HandleLoadingState()
  async savePref(): Promise<void> {
    const jurisNotify = normalizeDiaryYesNoPreference(
      this.fg.controls.jurisNotify.value,
      'N',
    );
    const allowStatusUpdate = normalizeDiaryYesNoPreference(
      this.fg.controls.allowStatusUpdate.value,
      'Y',
    );

    await Promise.all([
      this.api.preferences.setPreference({
        type: DIARY_JURIS_NOTIFY_TYPE,
        key: DIARY_JURIS_NOTIFY_KEY,
        value: jurisNotify,
      }),
      this.api.preferences.setPreference({
        type: DIARY_PREF_TYPE,
        key: DIARY_PREF_ALLOW_STATUS_UPDATE,
        value: allowStatusUpdate,
      }),
    ]);

    this.snackbar.show(
      'Shared diaries preferences saved successfully',
      'Success',
    );
    this.initialValues = { jurisNotify, allowStatusUpdate };
    this.hasChanges = false;
    this.close();
  }
}
